import isNil from 'lodash/isNil';
import {useRouter} from 'next/router';
import {HTMLAttributes, useCallback, useEffect, useState} from 'react';
import {OnlyInLoginChecker} from '../../../../common/checkers/OnlyInLoginChecker';
import {getCurrentUrlWithAdditonalParam} from '../../../../common/getCurrentUrlWithAdditonalParam';
import {t} from '../../../../common/getTranslations';
import {openWindow} from '../../../../common/openWindow.function';
import {requestContext} from '../../../../common/requestContext';
import {TRIGGER_COMMAND_KEY} from '../../../../common/shared/constants';
import {
  COMMAND_KEY,
  useCommandWithLocalStorage,
} from '../../../../common/shared/hooks/useCommandWithLocalStorage';
import {twMerge} from '../../../../common/twMerge';
import {ContentHomeCashTypeMoreButton} from '../../../../design-system/organisms/ContentHomeCashTypeMoreButton/ContentHomeCashTypeMoreButton';
import {ContentHomeTicketButton} from '../../../../design-system/organisms/ContentHomeTicketButton/ContentHomeTicketButton';
import {ContentHomeTicketButtonType} from '../../../../design-system/organisms/ContentHomeTicketButton/ContentHomeTicketButtonType.enum';
import {ReceiveFreeTicketDialog} from '../../../../design-system/organisms/ReceiveFreeTicketDialog/ReceiveFreeTicketDialog';
import {commonErrorHandler} from '../../../../errors/commonErrorHandler';
import {
  Bm,
  SaleMethod,
  SaleState,
  TicketKind,
} from '../../../../graphql/generated/types';
import {LOG_CLICK_EVENT} from '../../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../../logging/putTiaraObjectProps';
import {ONLY_DATA_CONFIG} from '../../../../reactQuery/constant';
import {useLogin} from '../../../../user/useLogin.hook';
import {useContentHomeOverviewQuery} from '../../../../wrapper/contentWrapper';
import {useContentMyTicketQuery} from '../../../../wrapper/ticketWrapper';
import {useReceiveTicketFree} from '../../../buy/ticket/hook/useReceiveTicketFree';
import {getDisplayWaitfreePeriodByMinute} from '../history/ticket/util/getDisplayWaitfreePeriodByMinute';
import {useContentHomeSetListDialog} from '../hook/useContentHomeSetListDialog';
import {isWaitfreeBm} from '../util/isWaitfreeBm';
import {showRefundDialog} from '../util/showRefundDialog';

export interface ContentHomeTabInfoContainerProps
  extends HTMLAttributes<HTMLDivElement> {
  seriesId: number;
}

export function ContentHomeTabInfoContainer({
  seriesId,
  className,
  ...props
}: ContentHomeTabInfoContainerProps) {
  const {isMobile} = requestContext.requestInfo.display;
  const [open, setOpen] = useState(false);
  const {isLogin, doLogin} = useLogin();
  const router = useRouter();
  const overview = useContentHomeOverviewQuery({seriesId}, ONLY_DATA_CONFIG)
    .data?.contentHomeOverview;
  const setList = overview?.setList;
  const todayGift = overview?.content?.serviceProperty?.todayGift;
  const isCashTypeHome = overview?.content?.saleMethod === SaleMethod.Cash;

  const {showDialog, dialogElem} = useContentHomeSetListDialog({
    isMobile: requestContext.requestInfo.display.isMobile,
    setList,
  });

  const {title, saleState, bm, waitfreePeriodByMinute, waitfreeBlockCount} =
    overview?.content ?? {};

  const contentMyTicket = useContentMyTicketQuery(
    {
      seriesId,
      includeWaitfree: true,
    },
    {enabled: isLogin && !!overview && !isCashTypeHome}
  ).data?.contentMyTicket;

  const onClickSetMore = useCallback(() => {
    if (setList) {
      if (setList.length > 1) {
        showDialog();
      } else {
        router.push(`/content/set-overview/${setList[0]?.id}`);
      }
    }
  }, [router, setList, showDialog]);

  const onClickTicketButton = useCallback(() => {
    router.push(`/buy/ticket?seriesId=${seriesId}`);
  }, [router, seriesId]);

  const onClickTicket = useCallback(() => {
    if (isMobile) {
      router.push(`/content/${seriesId}/history/ticket`);
      return;
    }

    openWindow({
      url: `https://${window.location.host}/content/${seriesId}/history/ticket`,
      target: t('more_ticket_history_title'),
      features: 'width=460,height=700',
    });
  }, [isMobile, router, seriesId]);

  const showTicketInfo =
    bm &&
    [
      Bm.FreePreview,
      Bm.Pay,
      Bm.PayWaitfree,
      Bm.MixedPay,
      Bm.MixedWaitfree,
    ].includes(bm) &&
    // 판매중이면 보여주고, 판매중지여도 이용권이 하나라도 있으면 보여준다
    (saleState === SaleState.Ing ||
      !!contentMyTicket?.ticketRentalCount ||
      !!contentMyTicket?.ticketOwnCount);

  const {result, execute, error} = useReceiveTicketFree({
    seriesId,
  });
  const {saveCommand, runCommand} = useCommandWithLocalStorage();
  const onClickTodayGift = useCallback(
    (parameters?: {fromSavedCommand?: boolean; todayGiftUid: number}) => {
      if (!parameters) {
        return;
      }
      if (isLogin) {
        execute(parameters.todayGiftUid);
      } else {
        if (!isLogin && !parameters.fromSavedCommand) {
          saveCommand({
            commandKey: COMMAND_KEY.CLICK_CONTENT_HOME_GIFT,
            command: {
              todayGiftUid: parameters.todayGiftUid,
              location: window.location.pathname,
            },
          }).then(() =>
            doLogin(
              getCurrentUrlWithAdditonalParam(
                TRIGGER_COMMAND_KEY,
                COMMAND_KEY.CLICK_CONTENT_HOME_GIFT
              )
            )
          );
        }
      }
    },
    [isLogin, execute, saveCommand, doLogin]
  );
  useEffect(() => {
    if (result) {
      setOpen(true);
    }
  }, [result]);
  useEffect(() => {
    if (error) {
      commonErrorHandler(error);
    }
  }, [error]);
  useEffect(() => {
    runCommand({
      commandKey: COMMAND_KEY.CLICK_CONTENT_HOME_GIFT,
      action: async commandData => {
        onClickTodayGift({
          todayGiftUid: commandData.todayGiftUid,
          fromSavedCommand: true,
        });
      },
    });
  }, [onClickTodayGift, runCommand]);

  if (!overview) {
    return null;
  }

  return (
    <div
      className={twMerge('mb-8pxr flex flex-col space-y-8pxr', className)}
      {...putTiaraObjectProps({
        click: {
          layer1: '이용권',
        },
      })}
      {...props}
    >
      {saleState !== SaleState.Ing &&
        ((!isCashTypeHome && isMobile) || isCashTypeHome) && (
          <ContentHomeTicketButton
            isMobile={isMobile}
            ticketButtonType={ContentHomeTicketButtonType.StopText}
          />
        )}
      {saleState === SaleState.Ing &&
        isCashTypeHome &&
        setList &&
        setList.length > 0 && (
          <>
            <ContentHomeCashTypeMoreButton
              isMobile={isMobile}
              maxDiscountRate={Math.max(
                ...setList.map(set => set?.discountRate || 0)
              )}
              {...putTiaraObjectProps({
                click: {layer1: '세트상품더보기'},
              })}
              onClickSetMore={onClickSetMore}
            />
            {dialogElem}
          </>
        )}
      {!isCashTypeHome && (
        <>
          {showTicketInfo && (
            <OnlyInLoginChecker>
              {saleState === SaleState.Ing ? (
                <ContentHomeTicketButton
                  isMobile={isMobile}
                  ticketButtonType={ContentHomeTicketButtonType.Ticket}
                  ownTicketCount={contentMyTicket?.ticketOwnCount}
                  rentalTicketCount={contentMyTicket?.ticketRentalCount}
                  onClickTicket={e =>
                    LOG_CLICK_EVENT('이용권_클릭', e, onClickTicket)
                  }
                  buttonEventLog={{
                    click: {layer1: '이용권충전'},
                  }}
                  onClickButton={e => {
                    LOG_CLICK_EVENT('이용권충전_클릭', e, onClickTicketButton);
                  }}
                  waitfreeProps={
                    isWaitfreeBm(bm) && !isNil(waitfreePeriodByMinute)
                      ? {
                          displayPeriodByMinute:
                            getDisplayWaitfreePeriodByMinute({
                              chargedPeriodByMinute:
                                contentMyTicket?.waitfree
                                  ?.chargedPeriodByMinute,
                              waitfreePeriodByMinute,
                            }),
                          waitfreeBlockCount,
                          bm,
                          isChargedComplete:
                            contentMyTicket?.waitfree?.chargedComplete,
                          chargedAt: contentMyTicket?.waitfree?.chargedAt
                            ? new Date(contentMyTicket?.waitfree?.chargedAt)
                            : undefined,
                        }
                      : undefined
                  }
                />
              ) : (
                <ContentHomeTicketButton
                  isMobile={isMobile}
                  ticketButtonType={ContentHomeTicketButtonType.Refund}
                  ownTicketCount={contentMyTicket?.ticketOwnCount}
                  rentalTicketCount={contentMyTicket?.ticketRentalCount}
                  onClickTicket={onClickTicket}
                  buttonEventLog={{
                    click: {layer1: '판매중지'},
                  }}
                  onClickButton={e => {
                    LOG_CLICK_EVENT('환불_클릭', e, () => {
                      e.preventDefault();
                      showRefundDialog({
                        isMobile: true,
                        onClickRefundButton: onClickTicket,
                      });
                    });
                  }}
                />
              )}
            </OnlyInLoginChecker>
          )}
          {saleState === SaleState.Ing &&
            todayGift &&
            !todayGift.isReceived && (
              <ContentHomeTicketButton
                isMobile={isMobile}
                ticketButtonType={ContentHomeTicketButtonType.Gift}
                giftTicketCount={todayGift.ticketCount}
                giftTicketKind={todayGift.ticketKind}
                buttonEventLog={{
                  click: {layer1: '선물받기', copy: '선물받기'},
                  customProps: {
                    event_ticket_type:
                      todayGift.ticketKind === TicketKind.Rent
                        ? '대여'
                        : '소장',
                  },
                }}
                onClickButton={e => {
                  LOG_CLICK_EVENT('선물받기_클릭', e, () =>
                    onClickTodayGift({todayGiftUid: todayGift.uid})
                  );
                }}
              />
            )}
          {saleState !== SaleState.Ing && !isMobile && (
            <ContentHomeTicketButton
              isMobile={isMobile}
              ticketButtonType={ContentHomeTicketButtonType.StopText}
            />
          )}
          {result && (
            <ReceiveFreeTicketDialog
              open={open}
              ticketKind={result.ticketKind}
              ticketExpireAt={result.ticketExpireAt}
              isMobile={requestContext.requestInfo.display.isMobile}
              title={t('contenthome_gift_popup_title')}
              contentTitle={title || ''}
              highlight={
                result.ticketKind === TicketKind.Own
                  ? t('ticket_purchase_confirmation_popup_possission_count', {
                      values: {value: result.ticketCount},
                    })
                  : t('ticket_purchase_confirmation_popup_rental_count', {
                      values: {value: result.ticketCount},
                    })
              }
              handleConfirm={() => setOpen(false)}
            />
          )}
        </>
      )}
    </div>
  );
}
